import type {AuthenticatedUser} from "auth/types";
import {isPage, isPageGroupWithChildren} from "router/getNavigation";
import routes from "router/routes";
import type {Page, PageGroup} from "router/types";
import {isRoleAtLeast} from "src/utils/roles";

export const getDefaultLandingPath = (
  user: AuthenticatedUser,
): string | null => {
  const findFirstLeaf = (
    items: Array<Page | PageGroup>,
    path: string[] = [],
  ): string | null => {
    for (const item of items) {
      if (item.hideFromNav) continue;

      // Check role access for this item
      if (
        item.roles &&
        !item.roles.some(role => isRoleAtLeast(user.role, role))
      ) {
        continue;
      }

      const currentPath = [...path, item.segment];

      if (isPageGroupWithChildren(item)) {
        const childPath = findFirstLeaf(item.children, currentPath);
        if (childPath) return childPath;
      } else if (isPage(item)) {
        return currentPath.join("/");
      }
    }
    return null;
  };

  return findFirstLeaf(routes);
};

export const isPathAccessibleToUser = (
  user: AuthenticatedUser,
  pathSegment: string,
): boolean => {
  const checkPath = (
    items: Array<Page | PageGroup>,
    remainingPath: string,
  ): boolean => {
    const [currentSegment, ...rest] = remainingPath.split("/");

    for (const item of items) {
      if (item.segment !== currentSegment) continue;

      // Check if the user's role satisfies the required roles
      if (
        item.roles &&
        !item.roles.some(role => isRoleAtLeast(user.role, role))
      ) {
        return false;
      }

      if (rest.length === 0) {
        return true;
      }

      if (isPageGroupWithChildren(item)) {
        return checkPath(item.children, rest.join("/"));
      }

      return false;
    }

    return false;
  };

  return checkPath(routes, pathSegment);
};
